import { getConsolidationGroupDisplayName } from "@common/helpers/entity";
import { ConsolidationGroupResponse } from "@common/schemas/domains/accounting/consolidation.schema";
import { Entity } from "@common/types/entity";
import { useEntities } from "@hooks/useEntities";
import { useLedgers } from "@api/ledger.api";
import { useScope } from "@state/scopeContext.lib";
import { resolveConsolidatedReportLedgerId } from "@components/ownership/ownershipConsolidationReports.helpers";
import { useReportConsolidationGroup } from "../useReportConsolidationGroup";

export interface ConsolidationReportContext {
    workspaceId: string;
    group: ConsolidationGroupResponse;
    parentEntity: Entity;
    ledgerId: string | undefined;
    displayName: string;
}

export type ConsolidationReportContextResult =
    | { status: "loading" }
    | { status: "absent" }
    | { status: "ready"; context: ConsolidationReportContext };

/**
 * Single source of truth for the workspace's consolidated reporting context.
 * Owns feature-flag gating, the group query, the parent-entity lookup, the
 * ledger resolution for the group basis, and the canonical `{Parent}
 * (Consolidated)` display name. Consumers (Reporting catalog, navigation,
 * breadcrumbs) read this instead of re-deriving the group/parent/ledger.
 */
export function useConsolidationReportContext(): ConsolidationReportContextResult {
    const { workspaceId } = useScope();
    const { consolidationGroup, isResolving } = useReportConsolidationGroup();
    const entities = useEntities();
    const parentEntity = entities.find(
        (entity) => entity.uuid === consolidationGroup?.parentEntityUuid,
    );
    const { data: ledgers, isLoading: isLoadingLedgers } = useLedgers(
        parentEntity?.id,
    );

    if (isResolving) {
        return { status: "loading" };
    }

    if (!consolidationGroup || !workspaceId) {
        return { status: "absent" };
    }

    // The group exists but its parent entity / ledgers are still loading.
    if (!parentEntity || isLoadingLedgers) {
        return { status: "loading" };
    }

    const ledgerId = resolveConsolidatedReportLedgerId({
        group: consolidationGroup,
        ledgers,
    });

    return {
        status: "ready",
        context: {
            workspaceId,
            group: consolidationGroup,
            parentEntity,
            ledgerId,
            displayName: `${getConsolidationGroupDisplayName({
                groupName: consolidationGroup.name,
                parentEntityName: parentEntity.name,
            })} (Consolidated)`,
        },
    };
}
